import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { RiBarChart2Fill, RiCheckFill, RiDownload2Fill, RiMoneyDollarCircleFill } from 'react-icons/ri'
import { useMutation } from 'react-query'
import { Pagination } from '../../components/Pagination'
import { usePermission } from '../../hooks/usePermission'
import { OpenAccountItem } from '../../Interfaces/openAccount'
import { api } from '../../services/api'
import { OpenAccountsFilters, useOpenAccounts } from '../../services/hooks/useOpenAccounts'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(Number(value ?? 0))

const formatDateDisplay = (isoDate: string) => {
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('pt-BR').format(date)
}

const todayISO = () => new Date().toISOString().slice(0, 10)

const startOfMonthISO = () => {
  const date = new Date()
  date.setDate(1)
  return date.toISOString().slice(0, 10)
}

const typeOptions = [
  { label: 'Todos', value: 'ALL' },
  { label: 'Despesas', value: 'DESPESA' },
  { label: 'Receitas', value: 'RECEITA' },
]

const categoryOptions = [
  { label: 'Todas', value: '' },
  { label: 'Empresa', value: 'EMPRESA' },
  { label: 'Pessoal', value: 'PESSOAL' },
  { label: 'Outros', value: 'OUTROS' },
]

const ITEMS_PER_PAGE = 10
const selectOptionStyle: CSSProperties = { color: '#1A202C', backgroundColor: '#FFFFFF' }

const OpenAccounts = () => {
  const toast = useToast()
  const { hasPermission } = usePermission()

  const [filters, setFilters] = useState<OpenAccountsFilters>({
    start_date: startOfMonthISO(),
    end_date: todayISO(),
    type: 'ALL',
  })

  const [clientFilter, setClientFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'DESPESA' | 'RECEITA'>('ALL')
  const [currentPage, setCurrentPage] = useState(1)

  const { data, isLoading, isFetching, refetch, error } = useOpenAccounts(filters)

  const [selectedEntry, setSelectedEntry] = useState<OpenAccountItem | null>(null)
  const [paymentDate, setPaymentDate] = useState(todayISO())
  const markAsPaidModal = useDisclosure()

  useEffect(() => {
    if (!hasPermission('financial.manage')) {
      toast({ description: 'Você não possui acesso a Contas em Aberto.', status: 'warning' })
    }
  }, [hasPermission, toast])

  const indicators = useMemo(() => {
    return {
      totalOpen: data?.meta.totalOpen ?? 0,
      clientsWithPending: data?.meta.clientsWithPending ?? 0,
      averageDelay: data?.meta.averageDelay ?? 0,
      topFive: data?.meta.topFive ?? [],
    }
  }, [data])

  const handleApplyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      start_date: prev.start_date || startOfMonthISO(),
      end_date: prev.end_date || todayISO(),
      client: clientFilter || undefined,
      category: categoryFilter || undefined,
      type: typeFilter,
    }))
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setClientFilter('')
    setCategoryFilter('')
    setTypeFilter('ALL')
    setFilters({
      start_date: startOfMonthISO(),
      end_date: todayISO(),
      type: 'ALL',
    })
    setCurrentPage(1)
  }

  const markAsPaidMutation = useMutation(
    async (payload: { demandId: number; serviceOrderId: number; payday: string }) => {
      await api.put(`/demands/${payload.demandId}/service-orders/${payload.serviceOrderId}/payment`, {
        payday: payload.payday,
      })
    },
    {
      onSuccess: async () => {
        toast({ description: 'Pagamento registrado com sucesso.', status: 'success' })
        await refetch()
        markAsPaidModal.onClose()
      },
      onError: () => {
        toast({ description: 'Não foi possível registrar o pagamento.', status: 'error' })
      },
    },
  )

  const handleMarkAsPaid = () => {
    if (!selectedEntry?.demandId || !selectedEntry.serviceOrderId) return

    markAsPaidMutation.mutate({
      demandId: selectedEntry.demandId,
      serviceOrderId: selectedEntry.serviceOrderId,
      payday: paymentDate,
    })
  }

  const handleExport = (format: 'csv' | 'pdf') => {
    if (format === 'csv') {
      const rows = data?.data ?? []
      if (!rows.length) {
        toast({ description: 'Nenhuma informação para exportar.', status: 'info' })
        return
      }

      const csvHeader = [
        'Tipo',
        'Descrição',
        'Valor Original',
        'Juros/Multa',
        'Total em Aberto',
        'Data de Vencimento',
        'Dias em Atraso',
        'Cliente',
        'OS vinculada',
        'Observação',
        'Categoria',
      ]

      const csvRows = rows.map((item) => [
        item.type,
        item.description ?? '',
        item.originalValue.toFixed(2),
        item.interestValue.toFixed(2),
        item.totalValue.toFixed(2),
        item.dueDate,
        item.daysInDelay,
        item.clientName ?? '',
        item.serviceOrderCode ?? '',
        item.observation ?? '',
        item.category ?? '',
      ])

      const csvContent = [csvHeader, ...csvRows].map((row) => row.join(';')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=UTF-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'contas_em_aberto.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      return
    }

    toast({ description: 'Exportação em PDF disponível em breve.', status: 'info' })
  }

  const list = data?.data ?? []
  const totalCount = data?.meta?.total ?? list.length

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE))
    if (currentPage > lastPage) {
      setCurrentPage(lastPage)
    }
  }, [totalCount, currentPage])

  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return list.slice(start, start + ITEMS_PER_PAGE)
  }, [list, currentPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" p="8">
      <Heading size="lg" fontWeight="normal" mb="6">
        Contas em Aberto
        {!isLoading && isFetching && <Spinner size="sm" color="gray.500" ml="4" />}
      </Heading>

      <VStack align="stretch" spacing={4} mb={6}>
        <SimpleGrid minChildWidth="220px" spacing={4}>
          <FormControl>
            <FormLabel>Data inicial</FormLabel>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(event) => setFilters((prev) => ({ ...prev, start_date: event.target.value }))}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Data final</FormLabel>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(event) => setFilters((prev) => ({ ...prev, end_date: event.target.value }))}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Cliente</FormLabel>
            <Input
              placeholder="Nome ou razão social"
              value={clientFilter}
              onChange={(event) => setClientFilter(event.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Tipo</FormLabel>
            <Select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value as 'ALL' | 'DESPESA' | 'RECEITA')}
              bg="gray.900"
              color="white"
              borderColor="gray.600"
              _hover={{ borderColor: 'pink.500' }}
              _focus={{ borderColor: 'pink.500' }}>
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value} style={selectOptionStyle}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Categoria</FormLabel>
            <Select
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              bg="gray.900"
              color="white"
              borderColor="gray.600"
              _hover={{ borderColor: 'pink.500' }}
              _focus={{ borderColor: 'pink.500' }}>
              {categoryOptions.map((option) => (
                <option key={option.value || 'all'} value={option.value} style={selectOptionStyle}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>

        <HStack spacing={3} justify="flex-end">
          <Button variant="ghost" onClick={handleClearFilters}>
            Limpar
          </Button>
          <Button colorScheme="pink" onClick={handleApplyFilters}>
            Filtrar
          </Button>
          <Button leftIcon={<Icon as={RiDownload2Fill} />} colorScheme="green" onClick={() => handleExport('csv')}>
            Exportar CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')}>
            Exportar PDF
          </Button>
        </HStack>
      </VStack>

      <Divider mb={6} borderColor="gray.700" />

      <SimpleGrid minChildWidth="220px" spacing={4} mb={8}>
        <Stat bg="gray.700" p={4} borderRadius="md">
          <StatLabel>Total em Aberto</StatLabel>
          <StatNumber display="flex" alignItems="center" gap={2}>
            <Icon as={RiMoneyDollarCircleFill} />
            {formatCurrency(indicators.totalOpen)}
          </StatNumber>
        </Stat>
        <Stat bg="gray.700" p={4} borderRadius="md">
          <StatLabel>Clientes com pendência</StatLabel>
          <StatNumber display="flex" alignItems="center" gap={2}>
            <Icon as={RiCheckFill} />
            {indicators.clientsWithPending}
          </StatNumber>
        </Stat>
        <Stat bg="gray.700" p={4} borderRadius="md">
          <StatLabel>Média de dias em atraso</StatLabel>
          <StatNumber display="flex" alignItems="center" gap={2}>
            <Icon as={RiBarChart2Fill} />
            {indicators.averageDelay}
          </StatNumber>
        </Stat>
      </SimpleGrid>

      <Box bg="gray.700" p={4} borderRadius="md" mb={6}>
        <Text fontWeight="bold" mb={3}>
          Top 5 valores em aberto
        </Text>
        {indicators.topFive.length === 0 ? (
          <Text fontSize="sm" color="gray.300">
            Nenhuma pendência encontrada para o período informado.
          </Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {indicators.topFive.map((entry, index) => (
              <Flex key={`${entry.description}-${index}`} justify="space-between">
                <Text>
                  {index + 1}. {entry.description || 'Sem descrição'} - {entry.clientName || 'Cliente não informado'}
                </Text>
                <Text fontWeight="bold">{formatCurrency(entry.totalValue)}</Text>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>

      {isLoading ? (
        <Flex justify="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Flex justify="center">
          <Text>Falha ao carregar as contas em aberto</Text>
        </Flex>
      ) : (
        <>
          <Table colorScheme="whiteAlpha">
            <Thead>
              <Tr>
                <Th>Tipo</Th>
                <Th>Descrição</Th>
                <Th isNumeric>Valor Original</Th>
                <Th isNumeric>Juros/Multa</Th>
                <Th isNumeric>Total em Aberto</Th>
                <Th>Vencimento</Th>
                <Th isNumeric>Dias em atraso</Th>
                <Th>Cliente</Th>
                <Th>OS vinculada</Th>
                <Th>Observação</Th>
                <Th>Categoria</Th>
                <Th textAlign="right">Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedList.map((item) => (
                <Tr key={item.id}>
                  <Td>
                    <Badge colorScheme={item.type === 'DESPESA' ? 'red' : 'green'}>{item.type}</Badge>
                  </Td>
                  <Td>{item.description}</Td>
                  <Td isNumeric>{formatCurrency(item.originalValue)}</Td>
                  <Td isNumeric>{formatCurrency(item.interestValue)}</Td>
                  <Td isNumeric>{formatCurrency(item.totalValue)}</Td>
                  <Td>{formatDateDisplay(item.dueDate)}</Td>
                  <Td isNumeric>{item.daysInDelay}</Td>
                  <Td>{item.clientName || '-'}</Td>
                  <Td>{item.serviceOrderCode || '-'}</Td>
                  <Td>{item.observation || '-'}</Td>
                  <Td>{item.category || '-'}</Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      {item.type === 'RECEITA' && item.demandId && item.serviceOrderId && (
                        <Button
                          size="sm"
                          colorScheme="pink"
                          onClick={() => {
                            setSelectedEntry(item)
                            setPaymentDate(todayISO())
                            markAsPaidModal.onOpen()
                          }}>
                          Marcar como pago
                        </Button>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {totalCount > ITEMS_PER_PAGE && (
            <Pagination
              totalCountOfRegisters={totalCount}
              registersPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      <Modal isOpen={markAsPaidModal.isOpen} onClose={markAsPaidModal.onClose}>
        <ModalOverlay />
        <ModalContent bg="gray.800">
          <ModalHeader>Registrar pagamento</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Informe a data de pagamento para a OS {selectedEntry?.serviceOrderCode} do cliente{' '}
              {selectedEntry?.clientName || '-'}.
            </Text>
            <Input type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={markAsPaidModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="pink" onClick={handleMarkAsPaid} isLoading={markAsPaidMutation.isLoading}>
              Confirmar pagamento
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default OpenAccounts
